import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import { useTracking } from '@folklore/tracking';
import classNames from 'classnames';
import { isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Barcode from 'react-barcode';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useLabels } from '../../contexts/LabelsContext';
import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import RedeemCouponModal from '../modals/RedeemCouponModal';
import Accordion from '../partials/Accordion';
import Body from '../partials/Body';
import PrizeImage from '../partials/PrizeImage';
import Timer from '../partials/Timer';

import styles from '../../../styles/prizes/coupon.module.scss';

const propTypes = {
    className: PropTypes.string,
    prize: AppPropTypes.prize,
    prizeTitle: PropTypes.string,
    withoutHeader: PropTypes.bool,
    withAccountLink: PropTypes.bool,
    withTitle: PropTypes.bool,
    cardBodyRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
    ]),
    onPrizeClaim: PropTypes.func,
};

const defaultProps = {
    className: null,
    prize: null,
    prizeTitle: null,
    withoutHeader: false,
    withAccountLink: false,
    withTitle: false,
    cardBodyRef: null,
    onPrizeClaim: null,
};

function Coupon({
    className,
    prize,
    prizeTitle,
    withoutHeader,
    withAccountLink,
    withTitle,
    cardBodyRef,
    onPrizeClaim,
}) {
    const intl = useIntl();
    const { redeemButton = null, redeemCouponTimeRemaining = null } = useLabels();
    const { name: themeName, isVariant } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const route = useUrlGenerator();
    const user = useUser();
    const { name: userName = null } = user || {};

    const {
        prize: prizeObject,
        used = false,
        usedAt = null,
        expiresAt = null,
        availableAt = null,
        available = false,
        claimed = false,
        coupon = null,
        showUserName = false,
    } = prize || {};

    const { upc = null } = coupon || {};

    const {
        name,
        description = null,
        instructions = null,
        note = null,
        legal = null,
    } = prizeObject || {};

    const tracking = useTracking();

    const parsedAvailableAt = useMemo(
        () => (availableAt !== null ? parseISO(availableAt) : null),
        [availableAt],
    );
    const parsedUsedAt = useMemo(() => (usedAt !== null ? parseISO(usedAt) : null), [usedAt]);
    const parsedExpiresAt = useMemo(
        () => (expiresAt !== null ? parseISO(expiresAt) : null),
        [expiresAt],
    );
    const isExpired = useMemo(
        () => parsedExpiresAt !== null && isAfter(new Date(), parsedExpiresAt),
        [parsedExpiresAt],
    );

    const [modalOpened, setModalOpened] = useState(false);

    const onButtonClick = useCallback(() => {
        setModalOpened(true);
    }, []);

    const onModalClose = useCallback(() => {
        setModalOpened(false);
    }, []);

    const onCouponRedeemed = useCallback(
        (user) => {
            if (onPrizeClaim !== null) {
                onPrizeClaim(user);
            }
            tracking.trackInteraction('prize_coupon_claim', {
                prize,
            });
        },
        [onPrizeClaim, tracking, prize],
    );

    const couponIsExpired =
        used || isExpired || (parsedUsedAt !== null && isAfter(new Date(), parsedUsedAt));
    const [expired, setExpired] = useState(couponIsExpired);

    const onCouponExpired = useCallback(() => {
        setExpired(true);
        tracking.trackInteraction('prize_coupon_redeemed', {
            prize,
        });
    }, [tracking, prize]);

    useEffect(() => {
        setExpired(couponIsExpired);
    }, [couponIsExpired, setExpired]);

    return (
        <div className={classNames([styles.container, themeClassName, className, 'prize-coupon'])}>
            {modalOpened ? (
                <RedeemCouponModal
                    prize={prize}
                    onComplete={onCouponRedeemed}
                    onClose={onModalClose}
                    closesOnSuccess
                />
            ) : null}
            {prizeTitle ? (
                <h4 className={classNames([styles.prizeTitle, 'prize-title'])}>{prizeTitle}</h4>
            ) : null}
            <div className={classNames([styles.inner, 'inner'])}>
                {/* {claimed && !expired ? null : <PrizeImage className={styles.image} prize={prize} />} */}
                <div className={classNames([styles.cardInner, 'card-inner'])}>
                    {!withoutHeader && withTitle ? (
                        <div className={classNames([styles.cardHeader, 'card-header'])}>
                            <div className={classNames([styles.title, 'title'])}>
                                <FormattedMessage
                                    defaultMessage="Coupon"
                                    description="Coupon title"
                                />
                            </div>
                        </div>
                    ) : null}
                    <div className={classNames([styles.cardBody, 'card-body'])} ref={cardBodyRef}>
                        <PrizeImage className={classNames([styles.image, 'image'])} prize={prize} />
                        <div className={classNames([styles.bodyInner, 'body-inner'])}>
                            {note !== null ? (
                                <div className={classNames([styles.note, 'note'])}>{note}</div>
                            ) : null}
                            {name !== null ? (
                                <div
                                    className={classNames([styles.name, 'name'])}
                                    dangerouslySetInnerHTML={{ __html: name }}
                                />
                            ) : null}
                            {description !== null ? (
                                <Body
                                    className={classNames([styles.description, 'description'])}
                                    body={description}
                                />
                            ) : null}
                            {!expired && instructions !== null ? (
                                <Body
                                    className={classNames([styles.instructions, 'instructions'])}
                                    body={instructions}
                                />
                            ) : null}
                            {!claimed ? (
                                <div className={classNames([styles.expires, 'expires'])}>
                                    {expired ? (
                                        <FormattedMessage
                                            defaultMessage="Expired on {date}"
                                            description="Coupon expires message"
                                            values={{
                                                date: (
                                                    <FormattedDate
                                                        value={parsedExpiresAt}
                                                        year="numeric"
                                                        month="2-digit"
                                                        day="2-digit"
                                                    />
                                                ),
                                            }}
                                        />
                                    ) : null}
                                    {!expired && parsedExpiresAt !== null ? (
                                        <FormattedMessage
                                            defaultMessage="Valid until {date}"
                                            description="Coupon expires message"
                                            values={{
                                                date: (
                                                    <FormattedDate
                                                        value={parsedExpiresAt}
                                                        year="numeric"
                                                        month="2-digit"
                                                        day="2-digit"
                                                    />
                                                ),
                                            }}
                                        />
                                    ) : null}
                                </div>
                            ) : null}

                            {claimed && expired ? (
                                <div className={classNames([styles.expires, 'expires'])}>
                                    <FormattedMessage
                                        defaultMessage="Used at {date}"
                                        description="Coupon message"
                                        values={{
                                            date: (
                                                <FormattedDate
                                                    value={parsedUsedAt}
                                                    year="numeric"
                                                    month="numeric"
                                                    day="numeric"
                                                    hour="numeric"
                                                    minute="numeric"
                                                />
                                            ),
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                    </div>

                    <div className={classNames([styles.cardFooter, 'card-footer'])}>
                        {claimed && !expired ? (
                            <div
                                className={classNames([styles.redeemedContent, 'redeemed-content'])}
                            >
                                {userName !== null && showUserName ? (
                                    <div className={styles.userName}>
                                        <FormattedMessage
                                            defaultMessage="Name: {name}"
                                            description="Coupon redeem user name"
                                            values={{
                                                name: userName,
                                            }}
                                        />
                                    </div>
                                ) : null}

                                <div
                                    className={classNames([
                                        styles.exchangeTimeRemainingLabel,
                                        'redeemed-description',
                                    ])}
                                >
                                    {redeemCouponTimeRemaining ?? (
                                        <FormattedMessage
                                            defaultMessage="Time remaining to redeem your coupon:"
                                            description="Coupon redeem time remaining label"
                                        />
                                    )}
                                </div>

                                <h5
                                    className={classNames([
                                        styles.exchangeTimeRemainingTimer,
                                        'redeemed-timer',
                                    ])}
                                >
                                    <Timer date={parsedUsedAt} started onEnded={onCouponExpired} />
                                </h5>
                                {upc !== null ? (
                                    <div className={classNames([styles.barCode, 'bar-code'])}>
                                        <Barcode value={upc} format="upc" width={2} height={100} />
                                    </div>
                                ) : null}
                            </div>
                        ) : null}
                        {!claimed && !expired && available ? (
                            <StyledButton
                                className={classNames([styles.claimButton, 'claim-button'])}
                                onClick={!withAccountLink ? onButtonClick : null}
                                href={withAccountLink ? route('account') : null}
                            >
                                {redeemButton || (
                                    <FormattedMessage
                                        defaultMessage="Redeem"
                                        description="Button label"
                                    />
                                )}
                            </StyledButton>
                        ) : null}
                        {!claimed && !expired && !available ? (
                            <StyledButton
                                className={classNames([
                                    styles.claimButton,
                                    styles.disabled,
                                    'claim-button',
                                ])}
                                disabled
                            >
                                {availableAt !== null ? (
                                    <>
                                        <FormattedMessage
                                            defaultMessage="Available on"
                                            description="Button label"
                                        />{' '}
                                        <FormattedDate
                                            value={parsedAvailableAt}
                                            year="numeric"
                                            month="numeric"
                                            day="numeric"
                                        />
                                    </>
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Available soon"
                                        description="Button label"
                                    />
                                )}
                            </StyledButton>
                        ) : null}
                    </div>
                    {legal !== null ? (
                        <Accordion
                            label={intl.formatMessage({
                                defaultMessage: 'Information',
                                description: 'Coupon accordion title',
                            })}
                            className={classNames([styles.accordion, 'accordion'])}
                            buttonClassName={classNames([
                                styles.accordionButton,
                                'accordion-button',
                            ])}
                            labelClassName={classNames([styles.accordionLabel, 'accordion-label'])}
                            iconClassName={classNames([styles.accordionIcon, 'accordion-icon'])}
                            contentClassName={classNames([
                                styles.accordionContent,
                                'accordion-content',
                            ])}
                        >
                            <Body className={styles.legal} body={legal} />
                        </Accordion>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

Coupon.propTypes = propTypes;
Coupon.defaultProps = defaultProps;

export default Coupon;
